import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ReactFlowProvider } from '@xyflow/react'
import { useIsActiveTab, useTabId } from '@/src/renderer/pages/TabStack/ActiveTabContext'
import { useCanvasFocus, type CanvasMode } from '@/src/renderer/contexts/CanvasFocusContext'
import { CanvasToolbar } from './CanvasToolbar'
import { CanvasWindowModeToggle } from './CanvasWindowModeToggle'
import { CanvasShareButton } from './CanvasShareButton'
import { CanvasProjectTitle } from './CanvasProjectTitle'
import { CanvasOffscreenHints } from './CanvasOffscreenHints'
import { CanvasMap } from './CanvasMap'
import { CanvasDragHandle } from './CanvasDragHandle'
import { CanvasFlow } from './CanvasFlow'
import { CanvasPeek } from './CanvasPeek'
import { useCanvasNotes } from './hooks/useCanvasNotes'
import { CANVAS_PEEK_ENTER_MS, CANVAS_PEEK_WIDTH, pickPeekNotes, shouldShowCanvasPeek } from './utils/canvasPeek'
import { buildTabOrder } from './utils/canvasTabOrder'
import { isConvertible } from './utils/noteToTask'
import { DEFAULT_CANVAS_NOTE_HEIGHT, DEFAULT_CANVAS_NOTE_WIDTH } from './utils/canvasNoteStyle'
import type { CanvasApplyApi } from './CanvasFlow'
import { useCanvasToolShortcut } from './hooks/useCanvasToolShortcut'
import { useCanvasPlacementShortcut } from './hooks/useCanvasPlacementShortcut'
import { useCanvasToolbarSide } from './hooks/useCanvasToolbarSide'
import { useCanvasToolbarDrag, type ToolbarDragGeometry } from './hooks/useCanvasToolbarDrag'
import { setCanvasToolbarSide } from './utils/canvasToolbarSide'
import type { CanvasPanelState, CanvasPlacementMode, CanvasTool } from './ProjectCanvas.types'
import {
  DEFAULT_SPLIT_RATIO,
  MOTION_FALLBACK_MS,
  RESIZE_ANIMATION_MS,
  type SplitDragResult,
} from './utils/canvasSplitDrag'
import {
  canvasSurfaceWidth,
  planCanvasContentMotion,
  predictCanvasContentWidth,
} from './utils/canvasMotionWidth'
import { watchPlacementDrag } from './utils/canvasPlacement'
import type { ProjectCanvasPanelProps } from './ProjectCanvasPanel.types'
import './ProjectCanvasPanel.scss'

/**
 * Eine Änderung an der Bewegung setzen, ohne dass sie jemand animiert.
 *
 * Der Browser fasst mehrere Zuweisungen im selben Bild zu EINER Stilberechnung
 * zusammen und sähe nur den Endwert. Wer einen Startwert braucht, muss ihn
 * dazwischen festschreiben — und das darf nichts auslösen.
 *
 * Der Hebel ist `--canvas-motion-duration`: Sie erbt sich vom Panel nach unten
 * und steht in JEDER Transition, die an der Bewegung hängt — die des gleitenden
 * Inhalts und die der linken Kante der Bedienschicht. Auf `0s` gesetzt liegen
 * alle zugleich still. Vorher stand hier ein inline gesetztes
 * `transition: none` auf einem einzelnen Element — das trug genau eines.
 */
function withoutMotion(root: HTMLElement | null, mutate: () => void): void {
  if (!root) return void mutate()
  root.style.setProperty('--canvas-motion-duration', '0s')
  mutate()
  // Diese eine Zeile ist der ganze Trick: Sie erzwingt eine Stilberechnung und
  // schreibt den Startwert damit fest.
  void root.offsetWidth
  root.style.removeProperty('--canvas-motion-duration')
}

/**
 * Projekt-Canvas panel — right-hand side of `ProjectPage`. Fährt die
 * closed/split/maximized-Zustandsmaschine, hält sie aber nicht: Der Zustand
 * kommt als Prop und geht über `onStateChange` zurück, weil er am **Projekt**
 * gemerkt wird (`canvasPanelState`) und `ProjectPage` ihn beim Öffnen von dort
 * holt. React Flow is only ever mounted while geöffnet, per the CLAUDE.md
 * TabStack `display:none` measurement gotcha (a collapsed panel keeps no RF
 * instance alive).
 *
 * @example
 * <ProjectCanvasPanel projectId={project.id} />
 */
export const ProjectCanvasPanel: React.FC<ProjectCanvasPanelProps> = ({
  projectId,
  state: requestedState,
  onStateChange,
  splitRatio = null,
  onSplitRatioCommit,
  showToolbar = true,
  peekSuppressed = false,
  coveredByDetails = false,
  openTarget = 'split',
  placementRequest = null,
  onPlacingChange,
  onApplyNotes,
  onOpenTask,
  revealRequest = null,
  projectName,
  projectNameHtml,
  projectIcon,
  projectIconColor,
  className,
}) => {
  // Der Zustand kommt von außen (siehe Props). `setPanelState` bleibt als
  // Name, damit die Handler unten unverändert lesen — nur dass es jetzt nach
  // oben meldet statt lokal zu setzen.
  const setPanelState = onStateChange
  const [tool, setTool] = useState<CanvasTool>('select')
  const [placementMode, setPlacementMode] = useState<CanvasPlacementMode>(null)
  // Ob gerade vom Werkzeug-Knopf GEZOGEN wird. Entscheidet, was absetzt: beim
  // Ziehen das Loslassen, sonst der nächste Klick auf die Fläche.
  const [placementDrag, setPlacementDrag] = useState(false)
  // Zähler statt Boolean: die 1:1-Ansicht darf beliebig oft hintereinander
  // angefordert werden, und ein `true`, das `true` bleibt, löste nur einmal
  // aus. Die Ansicht selbst kann nur CanvasFlow herstellen — nur dort gibt es
  // React Flows Instanz.
  const [resetZoomSignal, setResetZoomSignal] = useState(0)
  const notes = useCanvasNotes(projectId)
  /*
    Wer gerade ausgewählt ist. Der Canvas meldet es (`onSelectionChange`), das
    Panel macht daraus den Zustand des Übernehmen-Knopfs.
  */
  const [selectedNoteIds, setSelectedNoteIds] = useState<string[]>([])
  /** Was der Canvas zur **Übernahme** beisteuert — er befüllt den Ref selbst. */
  const applyApiRef = useRef<CanvasApplyApi | null>(null)

  /*
    Die ausgewählten Notizen, die sich JETZT übernehmen lassen — in
    **Lesereihenfolge**.

    Die Reihenfolge wird **allein über die Auswahl** gebaut und nicht über den
    ganzen Canvas: Sonst drehte eine Notiz, den man gar nicht ausgewählt hat,
    die Folge der ausgewählten (er könnte die Saat einer Zeile sein und einen
    der beiden mitnehmen). Wer drei Zettel einrahmt, meint diese drei, so wie
    er sie sieht.

    Dieselbe Regel wie ⇥ — `buildTabOrder`, nicht eine zweite Rechnung
    daneben: Zwei Lesereihenfolgen auf derselben Fläche liefen auseinander.
  */
  const applicableNotes = useMemo(() => {
    if (!notes || selectedNoteIds.length === 0) return []
    const selected = new Set(selectedNoteIds)
    const chosen = notes.filter(note => selected.has(note.id))
    /*
      `||` und nicht `??`, und das ist keine Stilfrage: `createCanvasNote`
      lässt für die Maße 0 zu, und ein Rechteck ohne Höhe überlappt NICHTS —
      auch sich selbst nicht (`buildTabOrder` vergleicht strikt). Die Saat
      käme dann nicht in ihre eigene Zeile, die Restliste schrumpfte nie und
      die Schleife liefe endlos. Dieselben Ersatzmaße wie in `noteToFlowNode`.
    */
    const order = buildTabOrder(chosen.map(n => ({
      id: n.id,
      x: n.x,
      y: n.y,
      width: n.width || DEFAULT_CANVAS_NOTE_WIDTH,
      height: n.height || DEFAULT_CANVAS_NOTE_HEIGHT,
    })))
    const byId = new Map(chosen.map(note => [note.id, note]))
    return order
      .map(id => byId.get(id))
      .filter((note): note is NonNullable<typeof note> => note !== undefined)
      .filter(isConvertible)
  }, [notes, selectedNoteIds])

  const handleApplyTasks = useCallback(() => {
    if (applicableNotes.length === 0) return
    void onApplyNotes?.(applicableNotes, applyApiRef.current)
  }, [applicableNotes, onApplyNotes])

  // Die Übernehmen-Pille an der Leiste EINES Zettels — nur dieser eine, und
  // nur, wenn er noch übernommen werden kann.
  const handleApplyNote = useCallback((noteId: string) => {
    const note = notes?.find(n => n.id === noteId)
    if (!note || !isConvertible(note)) return
    void onApplyNotes?.([note], applyApiRef.current)
  }, [notes, onApplyNotes])
  const isActiveTab = useIsActiveTab()
  const tabId = useTabId()
  const { setCanvasMode, clearCanvasMode, forecastRowGrowth } = useCanvasFocus()
  const rootRef = useRef<HTMLDivElement>(null)
  /**
   * Dasselbe Element noch einmal als State — für das **Drag Handle**.
   *
   * Es braucht das Panel schon in seinem ERSTEN Layout-Effekt, und ein Ref ist
   * dort noch leer: Layout-Effekte laufen von unten nach oben, das Handle liegt
   * im Baum innerhalb dieses `<div>`, und dessen Ref hängt React erst danach an.
   * Über den State kommt das Element als Prop nach, sobald es da ist, und die
   * Messung läuft ein zweites Mal — mit Erfolg.
   *
   * (Ohne das blieb der Griff ohne Lage stehen und war im geschlossenen Zustand
   * unsichtbar, bis irgendetwas anderes den Effekt erneut anstieß — etwa ein
   * Öffnen und Schließen des Canvas.)
   */
  const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null)
  const setRoot = useCallback((element: HTMLDivElement | null) => {
    rootRef.current = element
    setPanelElement(element)
  }, [])
  /**
   * Die Zwischenphase des Zuziehens — die Größe, AUS der geschlossen wird,
   * oder `null`.
   *
   * `closed` ist ein anderes Layout: Das Panel wird dort absolut positioniert
   * und die Canvas-Fläche fällt aus dem DOM. Von einer halben Spalte direkt
   * dorthin gäbe es nichts zu animieren, der Canvas verschwände schlagartig.
   * Stattdessen schrumpft er erst auf null — und erst danach wird daraus
   * wirklich `closed`.
   */
  const [closingFrom, setClosingFrom] = useState<Exclude<CanvasPanelState, 'closed'> | null>(null)
  const closing = closingFrom !== null

  /**
   * Die zuletzt GEMELDETE Absicht — der Ausgangspunkt des Zuziehens (und des
   * Öffnens aus dem Anriss).
   *
   * Steht hier oben und nicht beim Zuzieh-Effekt, obwohl er sie fortschreibt:
   * Die Renderphase braucht sie schon (siehe `closingStartsNow`), und ein
   * zweiter Ref daneben liefe irgendwann aus dem Takt.
   */
  const previousStateRef = useRef(requestedState)

  /**
   * Ob das Zuziehen GERADE in diesem Bild beginnt.
   *
   * `closingFrom` weiß es erst einen Commit später — es entsteht im
   * Zuzieh-Effekt weiter unten, und State aus einem Effekt gilt frühestens im
   * nächsten Durchlauf. Der Ref hingegen trägt in der Renderphase noch den Wert
   * VOR diesem Commit; beides zusammen ergibt die Zwischenphase ohne Lücke.
   *
   * Ohne diese Lücke zu schließen, geschah zweierlei in EINEM Task: Der erste
   * Commit hatte `panelState === 'closed'` — die Canvas-Fläche samt React Flow
   * wurde ABGEBAUT und der Anriss montiert —, der Effekt setzte `closingFrom`,
   * und der zweite Commit baute die Fläche NEU auf. Gezeichnet wurde nur der
   * zweite, aber mit einer frischen React-Flow-Instanz: Die Zettel
   * verschwanden nach ein, zwei Bildern, während die Spalte noch zulief. Und
   * für dieses eine Bild stünde auch die Größe auf `none`, die Breadcrumb also
   * zurück im Fluss — der Griff erzwingt in seinem Layout-Effekt eine
   * Stilberechnung.
   */
  const closingStartsNow = requestedState === 'closed' && previousStateRef.current !== 'closed'

  /**
   * Was das Panel ZEIGT — im Unterschied zu dem, was der Zustand SAGT.
   *
   * Beides fällt nur während des Zuziehens auseinander: Dort steht die Absicht
   * schon auf `closed`, während die Spalte noch zusammenläuft. Im ERSTEN Bild
   * des Zuziehens kommt die gezeigte Größe aus dem Ref (siehe
   * `closingStartsNow`), danach aus `closingFrom`.
   *
   * **Die Trennung ist der Kern dieser Datei.** Bis hierher hielt allein der
   * Drag Handle die Meldung zurück, indem er `closed` erst nach der Animation
   * nach oben gab — das × und das Tastenkürzel setzten sie sofort und sprangen
   * deshalb. Jetzt kommt die Absicht von allen dreien auf demselben Weg herein,
   * und das Zuziehen entsteht an EINER Stelle (siehe den Effekt unten).
   */
  const panelState: CanvasPanelState = closingFrom
    ?? (closingStartsNow ? previousStateRef.current : requestedState)
  const isOpen = panelState !== 'closed'
  /**
   * Der **Canvas-Anriss** — was `closed` zeigt, sobald Elemente da sind
   * (CONTEXT.md, „Canvas-Anriss"). Kein vierter Panel-Zustand: Was gemerkt,
   * per Tastenkürzel geschaltet und gezogen wird, bleibt `closed`. Nur die
   * Darstellung wechselt — und mit ihr die Größe, die dem Layout gemeldet wird.
   *
   * Am GEZEIGTEN Zustand, nicht an der Absicht: Beim Zuziehen steht die Absicht
   * schon auf `closed`, während die Spalte noch zusammenläuft. Der Anriss soll
   * erst kommen, wenn der Canvas wirklich weg ist — nicht daneben stehen,
   * während er noch schrumpft.
   */
  /*
    **Die Unterdrückung des Anrisses läuft durch EINE Tür.** `shouldShowCanvasPeek`
    wird hier an sechs Stellen gefragt — für die gezeigte Größe, fürs Zuziehen,
    fürs Öffnen, für den Boden des Zugs und für zwei Zeitgeber. Stünde die
    Prüfung nur an der sichtbaren, bliebe der Streifen in der Breite, in den
    Übergängen und im gemeldeten Layout-Modus weiter da.

    Über ein Ref, weil zwei der Aufrufer in Callbacks stecken, die den
    aktuellen Stand brauchen, ohne neu gebunden zu werden.
  */
  const peekSuppressedRef = useRef(peekSuppressed)
  peekSuppressedRef.current = peekSuppressed
  const canPeek = useCallback(
    (...args: Parameters<typeof shouldShowCanvasPeek>) =>
      !peekSuppressedRef.current && shouldShowCanvasPeek(...args),
    [],
  )

  const showPeek = !peekSuppressed && shouldShowCanvasPeek(panelState, notes)

  /**
   * Zuziehen MIT Elementen läuft auf die Breite des **Canvas-Anrisses**, nicht
   * auf null — und wird dort zu ihm (CONTEXT.md; Entscheidung mit Sven,
   * 2026-09-03). Die Klasse setzt die Zielbreite (ProjectCanvasPanel.scss).
   * An der ABSICHT (`'closed'` + Elemente), nicht am Gezeigten: Das zeigt ja
   * gerade noch die offene Größe.
   */
  const closingToPeek = closing && !peekSuppressed && shouldShowCanvasPeek('closed', notes)

  /**
   * Ob die Zettel des Anrisses gerade EINFAHREN — nur beim Zuziehen.
   *
   * Vom Panel gesetzt, nicht vom Anriss beim Mount: Die Bewegung gehört zur
   * Geste (Entscheidung mit Sven, 2026-09-03). Wer ein Projekt mit
   * geschlossenem Canvas aufruft, findet den Anriss einfach vor. Gesetzt wird
   * es mit dem BEGINN des Zuziehens — der Anriss liegt dann schon als Schicht
   * über der schrumpfenden Fläche, und seine Zettel blenden ein, während die
   * des Canvas ausblenden (Sven: „ineinander übergehen"). Ein Zeitgeber nimmt
   * es nach `CANVAS_PEEK_ENTER_MS` zurück; Öffnen räumt beides ab.
   */
  const [peekEntering, setPeekEntering] = useState(false)
  const peekEnterTimerRef = useRef<number | null>(null)

  /**
   * Die Spiegelphase des ÖFFNENS aus dem Anriss: Solange sie läuft, bleibt der
   * Anriss als **Schicht** über der wachsenden Canvas-Fläche stehen und
   * blendet aus, während der Inhalt einblendet (Entscheidung mit Sven,
   * 2026-09-03, Variante B). Dasselbe Muster wie das Zuziehen: `opening` aus
   * einem Effekt, `openingStartsNow` für das erste Bild aus dem Ref — sonst
   * fehlte die Schicht genau in dem Bild, in dem der Anriss verschwindet.
   *
   * Nur aus dem ANRISS, nicht aus der nackten Pille (leerer Canvas) und nicht
   * aus einem laufenden Zuziehen heraus (`closingFrom`): In beiden Fällen gab
   * es keine Zettel, aus denen etwas hervorkäme.
   */
  const [opening, setOpening] = useState(false)
  const openTimerRef = useRef<number | null>(null)
  const openingStartsNow = requestedState !== 'closed'
    && previousStateRef.current === 'closed'
    && closingFrom === null
    && !peekSuppressed
    && shouldShowCanvasPeek('closed', notes)
  const openingFromPeek = isOpen && (opening || openingStartsNow)

  /**
   * **Der Inhalt steht still, während die Kante läuft.**
   *
   * Solange dieser Zustand gilt, hängt der Canvas-Inhalt an der rechten Kante
   * und trägt eine FESTE Breite (`--canvas-content-width`,
   * ProjectCanvasPanel.scss). React Flow sieht damit keine Größenänderung und
   * rechnet während der ganzen Bewegung kein einziges Mal nach; die laufende
   * linke Kante gibt den Inhalt frei oder deckt ihn zu.
   *
   * Ohne das wanderten die Zettel mit der Kante — gemessen 659px quer durchs
   * Bild, und rund 170 Layout-Durchgänge je Bewegung.
   *
   * Er gilt für JEDE Größenbewegung des Panels, nicht nur für Auf und Zu:
   * `split ↔ maximiert` ist dieselbe Sache.
   */
  const [motion, setMotion] = useState(false)
  const motionTimerRef = useRef<number | null>(null)
  /** Das Abmelden vom `transitionend`, das die Bewegung beendet. */
  const motionUnlistenRef = useRef<(() => void) | null>(null)
  /**
   * Ob die Bewegung GERADE in diesem Bild beginnt — dieselbe Not wie bei
   * `closingStartsNow`: Der Zustand aus dem Effekt gälte einen Commit später,
   * und für dieses eine Bild stünde der Inhalt noch unverankert da.
   */
  const motionStartsNow = requestedState !== previousStateRef.current
  const inMotion = motion || motionStartsNow

  /**
   * Die Größe, die dem Layout gemeldet wird — sie ist NICHT der Panel-Zustand.
   *
   * | Größe | wann | was sie bewirkt |
   * |---|---|---|
   * | `split` / `maximized` | offen | Navigation weicht, Breadcrumb absolut |
   * | `closing` | läuft zu | Breadcrumb bleibt absolut, Navigation kommt zurück |
   * | `peek` | zu, mit Elementen | Breadcrumb absolut, Navigation bleibt |
   * | `none` | zu, leer | alles im Fluss |
   *
   * **`closing` gibt es, damit der Canvas beim Zuziehen seine HÖHE hält.** Die
   * Breadcrumb liegt bei offenem Canvas absolut über der Karte; im Fluss
   * beginnt der Tab-Slot um ihre Höhe tiefer. Fiele die Größe sofort auf
   * `none`, käme die Zeile zurück, bevor der Canvas weg ist — er verlöre oben
   * ~40px und schrumpfte sichtbar auch in der Höhe, statt nur schmaler zu
   * werden. Die Zeile hat dabei keinen Vorrang: Sie wartet, bis die Bewegung
   * durch ist.
   *
   * Die NAVIGATION hängt nicht hieran, sondern an `isCanvasFocused`
   * (`split`/`maximized`, siehe `CanvasFocusContext`) — sie fährt weiterhin
   * gemeinsam mit dem zulaufenden Canvas herein, nicht danach.
   */
  const layoutMode: CanvasMode = requestedState !== 'closed'
    ? (requestedState === 'maximized' ? 'maximized' : 'split')
    : (closing || closingStartsNow)
      ? 'closing'
      : showPeek ? 'peek' : 'none'

  // Tell MainLayout to clear the left navigation while this canvas is open —
  // und der ProjectPage, ob die Aufgabenliste bleibt (`split`) oder nach links
  // ausfährt (`maximized`).
  // Gated on `isActiveTab`: inactive tabs stay mounted behind `display: none`
  // (CLAUDE.md TabStack gotcha), so a background tab with an open canvas would
  // otherwise push the visible tab's sidebar away. The cleanup also covers
  // unmount and going inactive, so the navigation always comes back.
  //
  // `useLayoutEffect` und NICHT `useEffect` — die Meldung muss IM Commit
  // fallen, nicht danach. Ein passiver Effekt läuft erst, nachdem der Browser
  // gezeichnet hat, und daran hängt ein sichtbarer Sprung:
  //
  //   Frame N    Panel schon `--closed`, die Einstiegs-Pille hängt dran,
  //              aber `--canvas-open` steht noch. Die Breadcrumb-Zeile ist
  //              damit weiter aus dem Fluss genommen (MainLayout.scss).
  //   Frame N+1  Meldung durch, Zeile zurück im Fluss. Der Scroll-Ausschnitt
  //              wird um ihre Höhe kürzer und beginnt ebenso viel tiefer.
  //
  // Die Werkzeug-Pille hängt per `margin-block: auto` in der Mitte des Körpers
  // und rutschte dabei um die halbe Zeilenhöhe (~20px) nach unten — gemessen
  // waren es zwei falsch gezeichnete Frames. Als Layout-Effekt spült React das
  // `setCanvasMode` synchron nach, beide Klassenwechsel landen in EINEM Bild.
  //
  // Ungefährlich gegenüber der TabStack-Falle: Hier wird nichts gemessen, nur
  // ein Zustand gemeldet.
  //
  // WELCHE Größe gemeldet wird, steht oben bei `layoutMode` — samt der
  // Begründung, warum das Zuziehen eine eigene bekommt und die Navigation
  // trotzdem sofort zurückkommt.
  //
  // `none` wird nicht eigens gemeldet: Das erledigt der Aufräumer.
  //
  // Gemeldet wird für DIESEN Tab und unabhängig davon, ob er gerade vorn ist.
  // Der Kontext legt pro Tab ab, ein inaktives Panel überschreibt dem aktiven
  // also nichts — und genau darauf kommt es an: Als ein Wert für die App trug
  // die Seite eines geteilten Tabs beim Wechsel kurz den maximierten Zustand
  // des vorigen, und ihre Aufgabenliste fuhr von links herein. Siehe
  // `CanvasFocusContext`.
  useLayoutEffect(() => {
    if (layoutMode === 'none') return
    setCanvasMode(tabId, layoutMode)
    return () => clearCanvasMode(tabId)
  }, [tabId, layoutMode, setCanvasMode, clearCanvasMode])

  /*
    Escape bricht das Platzieren ab: Wer den Notiz-Knopf gedrückt hat und es
    sich anders überlegt, kommt sonst nur wieder heraus, indem er die Notiz
    absetzt und gleich wieder löscht.

    Nur bei scharfem Werkzeug registriert — sonst fischte dieser Listener
    Escapes ab, die anderen gehören.

    **In der Aufnahmephase, mit `stopPropagation`** — wie jede laufende Geste
    im Haus (`useSwapDrag`, `useCanvasToolbarDrag`, `CanvasOptionsBar`).
    Vorher horchte er in der Blasenphase und kam nie dran: Der Klick auf
    „Notiz" lässt den Fokus auf dem Knopf, und der hängt bei offenem Canvas IM
    `.react-flow` (ADR 0008). Von dort lief das Escape durch das
    Canvas-Element, wo `useCanvasEscape` es mit `stopPropagation` für die
    Auswahl beanspruchte — am Fenster kam nichts mehr an, der Zettel klebte
    weiter am Zeiger. Solange etwas scharf ist, gehört das Escape ihm.

    Was vorn läuft, muss die Ausnahmen selbst kennen: In der Aufnahmephase
    sind wir VOR dem Textfeld und vor jedem Menü, also gelten hier dieselben
    zwei Vorbehalte wie in `useCanvasEscape`.
  */
  useEffect(() => {
    if (!isActiveTab || placementMode === null) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Im Bearbeiten-Modus gehört Escape dem Textfeld: Es schließt dort den
      // Text ab.
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      // Einem offenen Menü seine Tasten lassen.
      if (document.querySelector('[role="menu"]')) return

      e.preventDefault()
      // Hält `useCanvasEscape` ab, im selben Zug die Auswahl aufzuheben.
      e.stopPropagation()
      setPlacementMode(null)
      setPlacementDrag(false)
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [isActiveTab, placementMode])

  // V/H wechseln das Werkzeug — nur bei offenem Canvas im sichtbaren Tab.
  // Der Hook sitzt hier statt in CanvasFlow, weil `setTool` hier lebt und
  // beide Bedingungen ohnehin schon vorliegen.
  useCanvasToolShortcut({ enabled: isActiveTab && isOpen, onSelectTool: setTool })

  const handleOpenPanel = useCallback(() => {
    setPanelState('split')
  }, [setPanelState])

  /** Der Maximieren-Knopf oben im **Canvas-Anriss** — gleich ganz groß. */
  const handleOpenMaximized = useCallback(() => {
    setPanelState('maximized')
  }, [setPanelState])

  const handleStartPlacingSticky = useCallback(() => {
    // Nur öffnen, wenn zu. Vorher stand hier ein unbedingtes `'split'`, was
    // aus dem maximierten Canvas heraus die Aufgabenliste zurückholte — der
    // Knopf soll aber nur eine Notiz scharfstellen, nicht das Fenster ändern.
    // An der Absicht: Wer mitten im Zuziehen den Knopf drückt, will ihn zurück.
    if (requestedState === 'closed') setPanelState(openTarget)
    setTool('select')
    setPlacementMode('note')
  }, [requestedState, setPanelState, openTarget])

  /**
   * Der Memo-Knopf — dasselbe wie bei der Notiz, nur eine andere Art.
   *
   * Er stand seit Phase 2 ohne Funktion da (siehe
   * `.claude/tasks/project-canvas.md`); jetzt legt er ab.
   */
  const handleStartPlacingMemo = useCallback(() => {
    if (requestedState === 'closed') setPanelState(openTarget)
    setTool('select')
    setPlacementMode('memo')
  }, [requestedState, setPanelState, openTarget])

  const handleStartPlacingArea = useCallback(() => {
    if (requestedState === 'closed') setPanelState('split')
    setTool('select')
    setPlacementMode('area')
  }, [requestedState, setPanelState])

  /** Der Text-Knopf — Fadenkreuz mit Platzhalter; Klick setzt ab, Zug zieht auf. */
  const handleStartPlacingText = useCallback(() => {
    if (requestedState === 'closed') setPanelState('split')
    setTool('select')
    setPlacementMode('text')
  }, [requestedState, setPanelState])

  /**
   * Den Knopf ANFASSEN stellt scharf — und merkt einen Zug erst VOR.
   *
   * Am Zeiger hängt sofort eine Notiz, das gilt für beide Wege. Ob daraus ein
   * Zug wird, entscheidet die Bewegung: Erst ab `PLACEMENT_DRAG_THRESHOLD`
   * legt das Loslassen ab (siehe `CanvasFlow`), vorher ist es ein Klick und
   * der nächste Klick auf die Fläche setzt ab.
   *
   * **Vorher galt der Zug ab dem Aufsetzen**, und daran hing der Fehler: Jeder
   * Klick lief durch den Ablege-Weg, das Loslassen setzte sofort eine leere
   * Notiz ab, und der Klick daneben stellte noch ein zweites Mal scharf. Man
   * bekam beides — eine Notiz unter dem Knopf UND ein Vorschaubild am Zeiger.
   */
  /*
    Anfassen eines Platzier-Knopfs — für beide Arten derselbe Zug. Scharf
    gestellt wird sofort (Antippen genügt); überschreitet die Hand die
    Schwelle, wird daraus ein Zug, den `CanvasFlow` beim Loslassen absetzt.
  */
  const handlePlacementPointerDown = useCallback((event: React.PointerEvent, startPlacing: () => void) => {
    startPlacing()
    watchPlacementDrag({ x: event.clientX, y: event.clientY }, () => setPlacementDrag(true))
  }, [])

  const handleStickyPointerDown = useCallback((event: React.PointerEvent) => {
    handlePlacementPointerDown(event, handleStartPlacingSticky)
  }, [handlePlacementPointerDown, handleStartPlacingSticky])

  const handleMemoPointerDown = useCallback((event: React.PointerEvent) => {
    handlePlacementPointerDown(event, handleStartPlacingMemo)
  }, [handlePlacementPointerDown, handleStartPlacingMemo])

  const handleAreaPointerDown = useCallback((event: React.PointerEvent) => {
    handlePlacementPointerDown(event, handleStartPlacingArea)
  }, [handlePlacementPointerDown, handleStartPlacingArea])

  const handleTextPointerDown = useCallback((event: React.PointerEvent) => {
    handlePlacementPointerDown(event, handleStartPlacingText)
  }, [handlePlacementPointerDown, handleStartPlacingText])

  /*
    `N` und `M` tun, was Notiz und Memo der **Listen-Werkzeug-Pille** tun —
    auch bei geschlossener Pinnwand, und dann geht sie auf `openTarget` auf
    (#229). Vom 11.09. bis dahin galten sie nur am offenen Canvas, weil `M`
    in der Liste die Aufmerksamkeit öffnete; die liegt jetzt auf `⇧M`.

    `T` und `⇧S` bleiben an der offenen Pinnwand: Ihre Knöpfe gibt es nur
    dort, und eine Taste soll nichts tun, wofür es keinen Knopf gibt.
  */
  useCanvasPlacementShortcut({ enabled: isActiveTab, closed: !isOpen, onPlaceSticky: handleStartPlacingSticky, onPlaceMemo: handleStartPlacingMemo, onPlaceArea: handleStartPlacingArea, onPlaceText: handleStartPlacingText })

  const handlePlacementConsumed = useCallback(() => {
    setPlacementMode(null)
    setPlacementDrag(false)
  }, [])

  /** Außerhalb der Fläche losgelassen — nichts anlegen. */
  const handlePlacementCancelled = useCallback(() => {
    setPlacementMode(null)
    setPlacementDrag(false)
  }, [])

  /**
   * Auf der Pille losgelassen: nur der Zug ist zu Ende, das Werkzeug bleibt
   * scharf. `placementMode` bleibt deshalb stehen — anders als beim Abbruch.
   */
  const handlePlacementDragEnd = useCallback(() => {
    setPlacementDrag(false)
  }, [])

  const handleClose = useCallback(() => {
    setPanelState('closed')
    setPlacementMode(null)
  }, [setPanelState])

  // Auch wenn von außen geschlossen wird (Tastenkürzel), muss ein
  // scharfgestelltes Werkzeug weg — sonst bliebe es in einem unsichtbaren
  // Canvas stehen und der nächste Klick auf die Aufgabenliste ginge ins Leere.
  useEffect(() => {
    if (requestedState === 'closed') {
      setPlacementMode(null)
      setPlacementDrag(false)
    }
  }, [requestedState])

  /*
    Notiz/Memo der **Listen-Werkzeug-Pille** (#229). Steht NACH dem
    Aufräum-Effekt: Bei geschlossener Pinnwand laufen beide im selben Commit,
    und der Aufräumer sieht noch `closed` — liefe er danach, löschte er das
    gerade scharf gestellte Werkzeug wieder.

    Gemerkt wird die zuletzt bediente `seq`, nicht ein Wahrheitswert: Dieselbe
    Anforderung, erneut gerendert, darf nicht noch einmal auslösen.
  */
  const handledPlacementSeqRef = useRef(placementRequest?.seq ?? null)
  useEffect(() => {
    if (!placementRequest || placementRequest.seq === handledPlacementSeqRef.current) return
    handledPlacementSeqRef.current = placementRequest.seq
    if (placementRequest.kind === 'note') handleStartPlacingSticky()
    else handleStartPlacingMemo()
    // Ein Zug aus der Pille: Die Schwelle hat die Seite schon gemessen, das
    // Loslassen über der Fläche legt ab (`CanvasFlow`).
    if (placementRequest.drag) setPlacementDrag(true)
  }, [placementRequest, handleStartPlacingSticky, handleStartPlacingMemo])

  /*
    Ein Zettel hängt am Zeiger — gemeldet an die Seite; die Listen-Werkzeug-Pille
    taucht ab, wenn SIE ihn angefordert hat (`ProjectPage`). Per Aufräumer gemeldet, damit auch ein Panel, das mitten im Setzen
    verschwindet (Stapel-Modus), „fertig“ sagt. Text und Bereich zählen nicht:
    Die legt die Pille nicht an.
  */
  const onPlacingChangeRef = useRef(onPlacingChange)
  onPlacingChangeRef.current = onPlacingChange
  const placingPaper = placementMode === 'note' || placementMode === 'memo'
  useEffect(() => {
    if (!placingPaper) return
    const report = onPlacingChangeRef.current
    report?.(true)
    return () => report?.(false)
  }, [placingPaper])

  const handleRequestResetZoom = useCallback(() => {
    setResetZoomSignal(n => n + 1)
  }, [])

  // ── Das Drag Handle ────────────────────────────────────────────────────
  //
  // Während des Ziehens gilt der lokale Wert, danach wieder der von außen.
  // Das Umschalten ist gefahrlos, weil beide im selben Ereignis gesetzt
  // werden: Der Aufrufer merkt sich das committete Verhältnis sofort (siehe
  // `ProjectPage`), es steht beim nächsten Rendern also schon in `splitRatio`.
  const [dragRatio, setDragRatio] = useState<number | null>(null)
  // Ob das Handle gerade zu sehen ist. Der linke **Randpfeil** weicht daran —
  // beide wollen die Mitte derselben Kante.
  const [handleActive, setHandleActive] = useState(false)

  const effectiveRatio = dragRatio ?? splitRatio ?? DEFAULT_SPLIT_RATIO
  const effectiveRatioRef = useRef(effectiveRatio)
  effectiveRatioRef.current = effectiveRatio
  /**
   * Das Verhältnis, das die Hand GERADE zeigt — nur während eines Zugs, sonst
   * `null`.
   *
   * Der Zug schreibt am State vorbei ins DOM (siehe `applyRatio`), damit er
   * nicht je Bewegung rendert. Rendert das Panel WÄHREND des Zugs trotzdem —
   * beim Wechsel `closed → split` am ersten Pixel, oder wenn ein Zeitgeber der
   * Spiegelphase fällt —, gleicht der Layout-Effekt unten das DOM nach dem
   * STATE ab und überschriebe die Hand mit dem gemerkten Wert: Die Kante
   * spränge auf die alte Breite, während man noch zieht. Solange dieser Ref
   * gesetzt ist, gilt er.
   */
  const liveRatioRef = useRef<number | null>(null)
  const panelStateRef = useRef(panelState)
  panelStateRef.current = panelState
  const splitRatioRef = useRef(splitRatio)
  splitRatioRef.current = splitRatio

  /**
   * Das Verhältnis ins DOM schreiben — am Panel für die Spaltenbreite (die
   * Regel steht im SCSS), am Dokument für alles außerhalb des Panels.
   *
   * **Imperativ und nicht als `style`-Prop, damit ein Zug am Griff KEIN Rendern
   * auslöst.** Das Panel ist nicht memoisiert und trägt `CanvasFlow` als Kind
   * — jede Zeigerbewegung wäre sonst ein vollständiges Rendern des Canvas
   * samt React-Flow-Hülle. Während des Zugs schreibt `handleDragChange` hier
   * direkt hinein; nach jedem Rendern gleicht der Layout-Effekt unten nach.
   */
  const applyRatio = useCallback((ratio: number) => {
    rootRef.current?.style.setProperty('--canvas-split-ratio', String(ratio))
    if (isActiveTab && panelStateRef.current !== 'closed') {
      document.documentElement.style.setProperty('--active-canvas-split-ratio', String(ratio))
    }
  }, [isActiveTab])

  /**
   * Die Zeile, gegen die das Verhältnis gerechnet wird: `.project-page__body`,
   * in der Aufgabenliste und Panel nebeneinander liegen.
   *
   * Über den Elternknoten und nicht über einen durchgereichten Ref: Die
   * Prozentbreite des Panels bezieht sich ohnehin auf genau dieses Element
   * (es ist sein Flex-Elternteil), Messung und Layout können also gar nicht
   * auseinanderlaufen.
   */
  const getRect = useCallback(() => {
    const parent = rootRef.current?.parentElement
    if (!parent) return null
    const { left, width } = parent.getBoundingClientRect()
    return { left, width }
  }, [])

  const getPanelState = useCallback(() => panelState, [panelState])

  /**
   * Die kleinste Canvas-Breite im Zug am **Drag Handle**: die Streifenbreite
   * des Anrisses, wenn Elemente liegen — dorthin läuft der Canvas beim
   * Loslassen ohnehin —, sonst null. Siehe `trackSplitDrag`.
   */
  const getDragFloor = useCallback(
    () => (canPeek('closed', notes) ? CANVAS_PEEK_WIDTH : 0),
    [notes, canPeek],
  )


  /**
   * Das Verhältnis des AKTIVEN Tabs am Dokument veröffentlichen.
   *
   * Die Breadcrumb liegt in `MainLayout` und damit außerhalb dieses Teilbaums;
   * eine Custom Property vererbt nach unten, nicht seitwärts. Sie braucht den
   * Wert aber: Bei offenem Canvas verlässt die Zeile den Fluss und bekommt eine
   * gerechnete Breite (`MainLayout.scss`) — stand dort weiterhin die feste
   * Hälfte, ragte sie mit ihrer eigenen Fläche über den Canvas, sobald die
   * Aufgabenliste schmaler gezogen wurde.
   *
   * Bewusst per DOM statt über den `CanvasFocusContext`: Der Wert ändert sich
   * während einer Zieh-Geste in jedem Frame, und über den Kontext zöge das ein
   * Rendern der gesamten App-Hülle nach sich. `setProperty` kostet nichts.
   *
   * Am Dokument und nicht am Panel, weil genau ein Tab sichtbar ist — dessen
   * Wert gilt. Das `isActiveTab`-Gate hält das sauber, das Aufräumen deckt
   * Tab-Wechsel, Schließen und Unmount ab.
   */
  useLayoutEffect(() => {
    applyRatio(liveRatioRef.current ?? effectiveRatio)
    if (!isActiveTab || !isOpen) return
    return () => { document.documentElement.style.removeProperty('--active-canvas-split-ratio') }
  }, [applyRatio, isActiveTab, isOpen, effectiveRatio])

  /**
   * Das Zuziehen — die EINE Stelle, an der es entsteht.
   *
   * Sie horcht auf die Absicht, nicht auf den Auslöser: Ob das × gedrückt
   * wurde, das Tastenkürzel oder der Drag Handle durchgezogen — sie alle setzen
   * den Zustand auf `closed`, und hier wird daraus eine Bewegung.
   *
   * Vorher gab es zwei Arten zu schließen: eine mit Zwischenphase (der Drag
   * Handle, der die Meldung selbst zurückhielt) und eine ohne (alle anderen,
   * die sprangen). Jetzt gibt es eine.
   *
   * **LAYOUT-Effekt**, damit die Klasse im selben Commit hängt, in dem der
   * Zustand umschlägt. Ein passiver Effekt käme nach dem Zeichnen — für ein
   * Bild stünde das Panel dann schon auf `--closed`, und genau dieser Sprung
   * soll ja weg.
   *
   * **Der Zeitgeber ist zugleich die Sperre.** Läuft er, ist das Zuziehen
   * unterwegs; ein zweites `closed` verlängert es nicht.
   */
  const closeTimerRef = useRef<number | null>(null)
  // Die Elemente für den Zuzieh-/Öffnen-Effekt, ohne ihn an `notes` zu
  // binden: Er soll auf die ABSICHT reagieren, nicht auf jede Änderung eines
  // Zettels.
  const notesRef = useRef(notes)
  notesRef.current = notes

  /**
   * Wie breit der Canvas-Inhalt während der Bewegung steht — die Breite, die
   * das Panel am ENDE haben wird.
   *
   * **Vorhergesagt, nicht gemessen**, und das geht nicht anders: Am Ziel ist
   * das Panel erst in 250ms, und bis dahin soll der Inhalt schon dort stehen,
   * wo er landen wird. Gemessen wird deshalb, was JETZT feststeht, und der
   * Rest gerechnet.
   *
   * **Gemessen wird die FLÄCHE, nicht das Panel** — sie liegt innerhalb der
   * beiden 1px-Ränder (`canvasSurfaceWidth`). Ohne diesen Abzug fror die
   * Fläche 2px zu breit ein, und der Inhalt sprang beim Auftauen um genau
   * diese 2px zurück: das Zucken im letzten Bild.
   *
   * **Sonst zählt allein die Zeile — so, wie sie am ENDE steht.** Meist ist
   * das die Zeile von jetzt. Im schmalen Fenster weicht aber die Navigation,
   * wenn die Pinnwand aufgeht (#296), und fährt im selben Takt hinaus; die
   * Zeile wächst dann während der Bewegung um 272px. Waren die Details offen,
   * kann sie mit deren Schließen auch zurückkommen. Das kennt nur
   * `MainLayout` (`rowGrowthOnCanvasOpen`, über `forecastRowGrowth`) — die
   * Meldung dieses Panels kommt dort erst NACH dieser Messung an. Ohne ihn stand der Inhalt
   * die ganzen 250ms 172px daneben und rastete im letzten Bild ein (#297,
   * gemessen bei 1024px). Dasselbe Muster wie einst der Zuschlag für die
   * eingeklappte Navigation, der seit `a4934e01` 272px zu weit setzte
   * (`.claude/tasks/canvas-zittern-beim-maximieren.md`).
   *
   * Gerechnet wird in `predictCanvasContentWidth`; hier wird nur gemessen.
   *
   * Ein Restfehler von ein, zwei Pixeln bleibt möglich; er fällt mit dem Ende
   * der Bewegung zusammen und ist damit unsichtbar. Ein E2E hält fest, dass
   * die Zettel über die ganze Bewegung stehen bleiben.
   */
  const measureSurfaceWidth = useCallback((state: CanvasPanelState, rowGrowth = 0): number | null => {
    // `closed` hat keine Fläche — weder eine gegenwärtige noch eine künftige.
    // Der Aufrufer nimmt dann die Breite der ANDEREN Seite; die Fläche bleibt
    // damit stehen und wird von der laufenden Kante freigelegt oder zugedeckt.
    if (state === 'closed') return null

    const panel = rootRef.current
    const row = panel?.parentElement
    if (!panel || !row) return null
    const rowWidth = row.getBoundingClientRect().width
    // TabStack-Falle (CLAUDE.md): Hinter `display: none` misst alles 0.
    if (rowWidth <= 0) return null

    return canvasSurfaceWidth(predictCanvasContentWidth({
      rowWidth: rowWidth + rowGrowth,
      ratio: effectiveRatioRef.current,
      state,
    }))
  }, [])

  /**
   * Die Bewegung abschließen: Verankerung und Versatz lösen.
   *
   * **Am `transitionend` der Breite, nicht an einer Uhr.** Ein
   * `setTimeout(250)` startet vor dem ersten Bild der Transition und läuft
   * darum ein Bild zu früh ab: Die Fläche taute auf, während das Panel noch
   * unterwegs war, der Inhalt sprang kurz nach rechts und mit dem letzten Bild
   * wieder zurück — das Zucken, das Sven zwei Bilder vor dem Ziel gesehen hat.
   * Der Zeitgeber bleibt als Rückfall: Läuft gar keine Transition (kein
   * Größenwechsel, versteckter Tab, `prefers-reduced-motion`), käme das
   * Ereignis nie.
   */
  const endMotion = useCallback(() => {
    if (motionTimerRef.current !== null) {
      window.clearTimeout(motionTimerRef.current)
      motionTimerRef.current = null
    }
    motionUnlistenRef.current?.()
    motionUnlistenRef.current = null
    setMotion(false)

    /*
      Breite und Versatz fallen GEMEINSAM und OHNE Übergang.

      Sie heben einander auf: Die Fläche ist um `W − Endbreite` zu breit, ihre
      linke Kante liegt also um genau diesen Betrag zu weit links — und um
      genau diesen Betrag sind Inhalt und Bedienschicht nach rechts gerückt.
      Fällt nur eines von beidem, steht der Inhalt für ein Bild um diese
      Strecke daneben; der Versatz trägt ja seine eigene 250ms-Transition, und
      die lief los, sobald die Eigenschaft verschwand. Gemessen: ein einzelnes
      Bild mit dem Zettel bei 1602 statt 1010 (2026-09-07).
    */
    withoutMotion(rootRef.current, () => {
      rootRef.current?.style.removeProperty('--canvas-content-width')
      rootRef.current?.style.removeProperty('--canvas-content-shift')
    })
  }, [])

  /**
   * Die Verankerung setzen und nach der Bewegung wieder lösen.
   *
   * Die Breite geht IMPERATIV ins DOM, nicht als `style`-Prop — dieselbe
   * Begründung wie beim Verhältnis (`applyRatio`): Das Panel trägt den Canvas
   * als Kind und ist nicht memoisiert; ein Rendern je Bewegung wäre genau das,
   * was hier eingespart werden soll.
   *
   * **Der Versatz kommt als Zweischritt**, beide Schritte im selben Bild:
   * setzen, festschreiben, auf den Endwert fahren. Ohne das Festschreiben
   * fasste der Browser beide Zuweisungen zu einer zusammen, sähe nur den
   * Endwert — und es gäbe nichts zu animieren.
   */
  const startMotion = useCallback((target: CanvasPanelState, previous: CanvasPanelState) => {
    const before = measureSurfaceWidth(previous)
    // Nur das Ziel liegt in der Zukunft. Ist die Pinnwand schon offen, ist die Navigation längst gewichen, und der
    // Zuwachs ist 0.
    const after = measureSurfaceWidth(target, forecastRowGrowth())
    // Beide Seiten zu: nichts einzufrieren. Sonst trägt die offene Seite auch
    // die Breite der geschlossenen — die Fläche bleibt dann stehen.
    if (before === null && after === null) return

    const plan = planCanvasContentMotion(
      before ?? after!,
      after ?? before!,
      // Gleiten nur zwischen zwei OFFENEN Zuständen. Auf- und Zuziehen haben
      // ihre eigene Choreografie (Anriss, Zettel-Schicht), und der Inhalt soll
      // dort stehen bleiben.
      before !== null && after !== null,
    )

    const root = rootRef.current
    root?.style.setProperty('--canvas-content-width', `${plan.width}px`)

    if (plan.shiftFrom !== plan.shiftTo) {
      withoutMotion(root, () => {
        root?.style.setProperty('--canvas-content-shift', `${plan.shiftFrom}px`)
      })
    }
    root?.style.setProperty('--canvas-content-shift', `${plan.shiftTo}px`)

    setMotion(true)

    if (motionTimerRef.current !== null) window.clearTimeout(motionTimerRef.current)
    motionUnlistenRef.current?.()
    if (root) {
      // Nur die Breite des PANELS zählt: `transitionend` blubbert, und im
      // Canvas läuft laufend irgendetwas anderes ab.
      const onEnd = (event: TransitionEvent) => {
        if (event.target !== root || event.propertyName !== 'width') return
        endMotion()
      }
      root.addEventListener('transitionend', onEnd)
      motionUnlistenRef.current = () => root.removeEventListener('transitionend', onEnd)
    }
    motionTimerRef.current = window.setTimeout(endMotion, RESIZE_ANIMATION_MS + MOTION_FALLBACK_MS)
  }, [measureSurfaceWidth, endMotion, forecastRowGrowth])

  useLayoutEffect(() => {
    const previous = previousStateRef.current
    previousStateRef.current = requestedState

    // Jede Größenänderung ist eine Bewegung — auch `split ↔ maximiert`, das
    // weder zuzieht noch öffnet.
    if (previous !== requestedState) startMotion(requestedState, previous)

    if (requestedState === 'closed') {
      // Ein Zuziehen mitten im Öffnen: Die Schicht des Öffnens hat nichts mehr
      // zu zeigen — der Canvas geht ja wieder zu.
      if (openTimerRef.current !== null) {
        window.clearTimeout(openTimerRef.current)
        openTimerRef.current = null
      }
      setOpening(false)

      if (previous === 'closed' || closeTimerRef.current !== null) return
      setClosingFrom(previous)
      // Mit Elementen fahren die Zettel des Anrisses ab JETZT ein — als Schicht
      // über der zulaufenden Fläche, nicht erst, wenn sie weg ist. Der Anriss
      // ist dann beim Umschalten schon halb da; bedienbar wird er erst dann.
      if (canPeek('closed', notesRef.current)) {
        setPeekEntering(true)
        if (peekEnterTimerRef.current !== null) window.clearTimeout(peekEnterTimerRef.current)
        peekEnterTimerRef.current = window.setTimeout(() => {
          peekEnterTimerRef.current = null
          setPeekEntering(false)
        }, CANVAS_PEEK_ENTER_MS)
      }
      closeTimerRef.current = window.setTimeout(() => {
        closeTimerRef.current = null
        setClosingFrom(null)
        // Was ein Zug an Verhältnis hinterlassen hat, gilt ab hier nicht mehr —
        // beim nächsten Öffnen kommt der gemerkte Wert vom Projekt. Auch im
        // DOM: Der Zug hat dort direkt geschrieben, und bleibt der gemerkte
        // Wert gleich, liefe der Layout-Effekt nicht noch einmal.
        setDragRatio(null)
        rootRef.current?.style.setProperty('--canvas-split-ratio', String(splitRatioRef.current ?? DEFAULT_SPLIT_RATIO))
      }, RESIZE_ANIMATION_MS)
      return
    }

    // Geöffnet — ob aus dem Anriss, aus der Pille oder mitten im Zuziehen.
    // Einfahrende Zettel gibt es ab hier nicht mehr.
    if (peekEnterTimerRef.current !== null) {
      window.clearTimeout(peekEnterTimerRef.current)
      peekEnterTimerRef.current = null
    }
    setPeekEntering(false)

    // Aus dem Anriss geöffnet: die Schicht stehen lassen, bis die Bewegung
    // durch ist (siehe `openingStartsNow` — dasselbe Bild, in dem der Anriss
    // aus dem Baum fällt). Aus der Pille oder aus dem Zuziehen gibt es keine.
    if (previous === 'closed' && closeTimerRef.current === null && canPeek('closed', notesRef.current)) {
      setOpening(true)
      if (openTimerRef.current !== null) window.clearTimeout(openTimerRef.current)
      openTimerRef.current = window.setTimeout(() => {
        openTimerRef.current = null
        setOpening(false)
      }, RESIZE_ANIMATION_MS)
    }

    /*
      Wieder aufgemacht, während es noch zuzog.

      Das kann aus zwei Richtungen kommen: aus derselben Zieh-Geste (wer
      durchzieht und wieder nach links geht, bekommt den Canvas zurück — ADR
      0010) oder aus einem zweiten Tastendruck. Ohne diesen Abbruch liefe der
      Zeitgeber weiter und schlösse 250ms später doch noch.

      `dragRatio` wird hier bewusst NICHT angefasst: Kommt der Abbruch aus einer
      laufenden Geste, hat die ihren Wert im selben Ereignis schon gesetzt — ihn
      hier zurückzunehmen risse die Spalte auf die halbe Breite. Die Schrumpfung
      hängt an der Klasse `--closing`, nicht an einer Zahl, deshalb bleibt beim
      Abbrechen nichts zurückzusetzen.
    */
    if (closeTimerRef.current === null) return
    window.clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
    setClosingFrom(null)
  }, [requestedState, startMotion, canPeek])

  // Ein Abbau mitten im Zuziehen — Projektwechsel, Tab zu — darf den Zeitgeber
  // nicht stehen lassen.
  useEffect(() => () => {
    if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current)
    if (peekEnterTimerRef.current !== null) window.clearTimeout(peekEnterTimerRef.current)
    if (openTimerRef.current !== null) window.clearTimeout(openTimerRef.current)
    if (motionTimerRef.current !== null) window.clearTimeout(motionTimerRef.current)
    motionUnlistenRef.current?.()
  }, [])

  /** Während des Ziehens: nur Ansicht, nichts schreiben. */
  const handleDragChange = useCallback((result: SplitDragResult) => {
    // Auch das Zuziehen ist nur ein Zustand — die Bewegung dazu entsteht im
    // Effekt oben, gemeinsam mit dem × und dem Tastenkürzel. Nur bei einem
    // WECHSEL: Der Setter gehört der Seite, und ein gleicher Wert je Bewegung
    // wäre dort trotzdem ein Rendern.
    if (result.state !== panelStateRef.current) setPanelState(result.state)
    // Das Verhältnis geht am State vorbei direkt ins DOM (siehe `applyRatio`).
    // Festgeschrieben wird es erst beim Loslassen (`handleDragCommit`).
    if (result.state !== 'closed' && result.ratio !== null) {
      liveRatioRef.current = result.ratio
      applyRatio(result.ratio)
    }
  }, [setPanelState, applyRatio])

  /** Beim Loslassen: der eine Schreibvorgang. */
  const handleDragCommit = useCallback((result: SplitDragResult) => {
    // Die Hand hat losgelassen — ab hier gilt wieder der State.
    liveRatioRef.current = null
    // Unter der Schließschwelle losgelassen: ERST JETZT wird daraus `closed`
    // (`commitSplitDrag`) — im Zug selbst folgte der Canvas der Hand bis auf
    // die Streifenbreite. Das Zuziehen von hier auf 77 entsteht im Effekt
    // oben, wie beim × und beim Tastenkürzel. Festzuschreiben gibt es nichts:
    // Das Verhältnis hat dieser Zug nicht angefasst.
    if (result.state === 'closed') {
      setPanelState('closed')
      return
    }
    setPanelState(result.state)
    // Der gezogene Wert bleibt lokal stehen, bis der Aufrufer ihn übernimmt —
    // ohne `onSplitRatioCommit` spränge die Spalte sonst beim nächsten Rendern
    // auf den alten Wert zurück, denn das DOM kennt nur noch den Zug.
    setDragRatio(result.ratio)
    // `null` heißt hier „dieser Zug hat das Verhältnis nicht angefasst" — beim
    // Zuklappen und beim Zuziehen. Es zu schreiben hieße, die Spalte beim
    // nächsten Öffnen auf ihrer Mindestbreite zurückzuholen statt dort, wo sie
    // zuletzt stand.
    if (result.ratio !== null) onSplitRatioCommit?.(result.ratio)
  }, [onSplitRatioCommit, setPanelState])

  /**
   * Doppelklick, eine Regel für alle drei Größen (CONTEXT.md, ADR 0010):
   *
   * > Der Doppelklick geht eine Stufe HÖHER: zu → halbe/halbe → maximiert.
   * > Steht die Aufteilung nicht auf halbe/halbe, ist sie die nächste Stufe;
   * > steht sie dort schon, folgt `maximiert`. Aus `maximiert` führt er zurück
   * > auf halbe/halbe.
   *
   * Vorher setzte der zweite Doppelklick nur die Breite zurück — eine Geste,
   * die nichts tat, wenn man die Breite nie verstellt hatte. Die Größe zu
   * wechseln beantwortet die Frage, die man an einer Kante stellt, in jedem
   * Zustand.
   *
   * **Eine verstellte Breite ist eine eigene Stufe.** Ohne sie wäre der Sprung
   * von einer schmalen Spalte auf den ganzen Bildschirm der erste, den die
   * Geste tut, und die Stufe dazwischen — die, die man eigentlich meint —
   * gäbe es gar nicht.
   *
   * Beide Richtungen gleich: `effectiveRatio` ist der Anteil der
   * AUFGABENLISTE, ein schmaler Canvas also ein großes Verhältnis und ein
   * breiter ein kleines. Die Geste soll das nicht unterscheiden müssen —
   * gemessen wird der ABSTAND zur Hälfte.
   *
   * **Geteilt heißt dabei halbe/halbe**, aus `maximiert` wie aus
   * `geschlossen`: Der Doppelklick stellt eine Größe her, keine gemerkte
   * Vorliebe — wer eine eigene Breite will, zieht. Gemeldet wird `null` und
   * nicht die 0.5: Das heißt „keine gemerkte Breite" und ergibt dieselbe
   * Hälfte, ohne eine Zahl zu erfinden, die niemand eingestellt hat.
   *
   * Nur diese Geste tut das. Das **Fenster-Knopfpaar** und `⇧C` holen die
   * gemerkte Breite weiterhin zurück — sie sprechen von der Größe, nicht von
   * der Aufteilung.
   */
  const handleDoubleClick = useCallback(() => {
    setDragRatio(null)
    // Ein Verhältnis, das nur um Haaresbreite daneben liegt, soll die Stufe
    // nicht ein zweites Mal einlegen: Gezogene Werte sind krumme Zahlen.
    const istHalbe = Math.abs(effectiveRatio - DEFAULT_SPLIT_RATIO) <= 0.01
    if (panelState === 'split' && istHalbe) {
      setPanelState('maximized')
      return
    }
    setPanelState('split')
    onSplitRatioCommit?.(null)
  }, [effectiveRatio, onSplitRatioCommit, panelState, setPanelState])

  /**
   * Die Breite der Aktionsgruppe, gemessen.
   *
   * Der obere **Randpfeil** sitzt waagerecht mittig und muss ihr bei schmalem
   * Canvas ausweichen — dafür braucht er ihre Breite. Fest setzen ließe sie
   * sich nicht: Sie hängt am übersetzten Text („Freigeben" / „Share").
   *
   * Anders als die `--canvas-viewport-height`-Messung, die hier einmal als
   * Ballast wieder ausgezogen ist, misst diese etwas, das sich tatsächlich
   * ändert.
   */
  const [actionsWidth, setActionsWidth] = useState<number | undefined>(undefined)
  const actionsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = actionsRef.current
    if (!element) return

    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.borderBoxSize?.[0]?.inlineSize ?? element.offsetWidth
      // Nur bei echter Änderung: Der Beobachter meldet auch, wenn sich nichts
      // bewegt hat, und jedes `setState` zöge ein Rendern des Panels nach sich.
      setActionsWidth(previous => (previous === width ? previous : width))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [isOpen])

  const handleToggleMaximize = useCallback(() => {
    setPanelState(panelState === 'maximized' ? 'split' : 'maximized')
  }, [panelState, setPanelState])

  /*
    Die Pille sitzt in einer eigenen Platte statt direkt im Panel. Grund:
    `.canvas-toolbar` trägt ihren Schatten als `filter: drop-shadow`, der der
    Silhouette der Segmente folgt — ein Hintergrund auf demselben Element
    würde ihn zu einem Rechteck um die Fläche herum aufziehen.

    Die Platte selbst ist nur Geometrie: Ihre Fläche liegt in einem `::before`
    UNTER die Notiz, die Pille darüber (ProjectCanvasPanel.scss).

    **Nur an der offenen Pinnwand** (#229). Geschlossen und im Anriss stand
    hier bis dahin eine kleine Pille; in die Pinnwand führen seitdem das Drag
    Handle, `C` und Notiz/Memo der **Listen-Werkzeug-Pille**.

    `showToolbar: false` in einem noch leeren Projekt: Dort steht der
    Empty-State-Teaser, und neben einer Einladung soll keine Bedienung schweben.
    Das **Drag Handle** bleibt trotzdem — es ruht unsichtbar an der Kante und
    nimmt dem Teaser nichts (ADR 0010).
  */
  /*
    Die Seite der Pille — links oder rechts, gerätelokal für alle Projekte,
    und jedes offene Panel folgt sofort (CONTEXT.md, Werkzeug-Pille).

    Der Zug am **Griff** bewegt NUR die Pille (`.canvas-toolbar`, per
    `--drag-x`/`--drag-y`), nicht die Platte: Die darf keinen `transform`
    tragen, sonst schlösse sie ihre Kinder in einen Stacking-Kontext ein
    (siehe ProjectCanvasPanel.scss). Ihre Fläche bleibt stehen und wird
    währenddessen selbst zur Ableg-Fläche am Ursprung.

    Gemessen wird beim Zugbeginn: Pille, Platte — und die Gegen-Fläche als
    Spiegelbild der Platte im Canvas. Die Gegen-Fläche selbst ist zu dem
    Zeitpunkt noch nicht im DOM, und sie muss es auch nicht sein: Sie ist
    das Spiegelbild, also folgt ihr Rechteck aus dem der Platte.
  */
  const toolbarSide = useCanvasToolbarSide()
  const toolbarPlateRef = useRef<HTMLDivElement>(null)
  const toolbarPillRef = useRef<HTMLDivElement>(null)
  // Die Höhe der Platte beim Zugbeginn — die Gegen-Fläche bekommt sie als
  // Variable, denn ohne Inhalt hätte sie keine eigene.
  const toolbarPlateHeightRef = useRef(0)

  const measureToolbarDrag = useCallback((): ToolbarDragGeometry | null => {
    const plate = toolbarPlateRef.current
    const pill = toolbarPillRef.current
    const canvas = plate?.parentElement
    if (!plate || !pill || !canvas) return null
    const origin = plate.getBoundingClientRect()
    const frame = canvas.getBoundingClientRect()
    toolbarPlateHeightRef.current = origin.height
    // Gespiegelt an der senkrechten Mitte des Canvas: derselbe Abstand zur
    // anderen Kante.
    const mirroredLeft = frame.left + (frame.right - origin.right)
    const target = new DOMRect(mirroredLeft, origin.top, origin.width, origin.height)
    return { pill: pill.getBoundingClientRect(), origin, target }
  }, [])

  const toolbarDrag = useCanvasToolbarDrag({
    side: toolbarSide,
    pillRef: toolbarPillRef,
    measure: measureToolbarDrag,
    onSideChange: setCanvasToolbarSide,
  })
  const toolbarDragging = toolbarDrag.phase !== 'idle'

  const toolbarPlateClass = [
    'project-canvas-panel__toolbar-plate',
    toolbarSide === 'right' && 'project-canvas-panel__toolbar-plate--right',
    toolbarDragging && 'project-canvas-panel__toolbar-plate--dragging',
    toolbarDrag.phase === 'dragging' && 'project-canvas-panel__toolbar-plate--lifted',
    toolbarDrag.nearest === 'origin' && 'project-canvas-panel__toolbar-plate--armed',
  ].filter(Boolean).join(' ')

  const toolbarPlate = !showToolbar ? null : (
    <div ref={toolbarPlateRef} className={toolbarPlateClass}>
      <CanvasToolbar
        ref={toolbarPillRef}
        variant="full"
        side={toolbarSide}
        tool={tool}
        onOpenPanel={handleOpenPanel}
        onStartPlacingSticky={handleStartPlacingSticky}
        onStickyPointerDown={handleStickyPointerDown}
        onStartPlacingMemo={handleStartPlacingMemo}
        onMemoPointerDown={handleMemoPointerDown}
        onStartPlacingArea={handleStartPlacingArea}
        onAreaPointerDown={handleAreaPointerDown}
        onStartPlacingText={handleStartPlacingText}
        onTextPointerDown={handleTextPointerDown}
        onToolChange={setTool}
        onResetZoom={handleRequestResetZoom}
        onGripPointerDown={toolbarDrag.gripPointerDown}
        gripPressed={toolbarDrag.phase === 'dragging'}
        /*
          Erst **ab zwei** ausgewählten Zetteln: Die Einzelübernahme hat ihre
          eigene Pille am Zettel, ein zweiter Weg dafür wäre nur Rauschen.
          Gezählt werden die noch übernehmbaren (`applicableNotes` filtert mit
          `isConvertible`) — sind beide Ausgewählten längst Aufgaben, bleibt die
          Pille kurz, statt einen Knopf ohne Ziel zu zeigen.
        */
        applyCount={selectedNoteIds.length >= 2 ? applicableNotes.length : 0}
        onApplyTasks={onApplyNotes ? handleApplyTasks : undefined}
      />
    </div>
  )

  /*
    Die **Ableg-Fläche** auf der Gegenseite — nur während des Zugs. Ein
    Geschwister der Platte, ÜBER die Notiz wie die Pille
    (`$canvas-control-layer`, ProjectCanvasPanel.scss). Die Höhe kommt aus der Messung, denn sie hat
    keinen Inhalt, an dem sie wachsen könnte.
  */
  const toolbarTarget = toolbarDragging ? (
    <div
      className={`project-canvas-panel__toolbar-target project-canvas-panel__toolbar-target--${toolbarSide === 'left' ? 'right' : 'left'}${toolbarDrag.nearest === 'target' ? ' project-canvas-panel__toolbar-target--armed' : ''}`}
      style={{ '--plate-height': `${toolbarPlateHeightRef.current}px` } as React.CSSProperties}
      aria-hidden="true"
    />
  ) : null

  return (
    <div
      ref={setRoot}
      className={`project-canvas-panel project-canvas-panel--${showPeek ? 'peek' : panelState}${closing ? ' project-canvas-panel--closing' : ''}${closingToPeek ? ' project-canvas-panel--closing-to-peek' : ''}${openingFromPeek ? ' project-canvas-panel--opening-from-peek' : ''}${inMotion ? ' project-canvas-panel--motion' : ''}${className ? ` ${className}` : ''}`}
      /* `--canvas-split-ratio` kommt imperativ (siehe `applyRatio`), nicht
         als `style`-Prop — die Breitenregel selbst steht im SCSS. */
    >
      {isOpen && (
        <div className="project-canvas-panel__canvas">
          {/*
            Der Kasten, der den Inhalt während einer Bewegung an der rechten
            Kante festhält (ProjectCanvasPanel.scss, `__viewport`). Er trägt
            selbst nichts — er ist nur die Stelle, an der die feste Breite
            angreifen kann: An `.canvas-flow` ginge das nicht, dort setzt
            React Flow `position: relative` inline.
          */}
          <div className="project-canvas-panel__viewport">
          <ReactFlowProvider>
            {/*
              Beide Bedienelemente hängen IM Canvas, nicht daneben (ADR 0008):
              Nur als Kind von `.react-flow` landet ihre Fläche im selben
              Stacking-Kontext wie Punktraster und Notizen und kann sich
              dazwischenschieben. Von außen ist die Canvas-Ebene ein
              geschlossener Block — dort verdeckte die Fläche jeden Notizen.
            */}
            <CanvasFlow
              projectId={projectId}
              notes={notes ?? []}
              tool={tool}
              placementMode={placementMode}
              onPlacementConsumed={handlePlacementConsumed}
              placementDrag={placementDrag}
              onPlacementCancelled={handlePlacementCancelled}
              onPlacementDragEnd={handlePlacementDragEnd}
              isActiveTab={isActiveTab}
              resetZoomSignal={resetZoomSignal}
              onSelectionChange={setSelectedNoteIds}
              onOpenTask={onOpenTask}
              revealRequest={revealRequest}
              applyApiRef={applyApiRef}
              onApplyNote={onApplyNotes ? handleApplyNote : undefined}
              onApplyApplicable={onApplyNotes ? handleApplyTasks : undefined}
            >
              {/* Figma „Action Group" (4956:104724): „Freigeben" links, die
                  Fenster-Umschaltgruppe rechts, 16px dazwischen. Eine Platte
                  über beide — bei 8px Rand stießen zwei getrennte ohnehin
                  aneinander. */}
              {/*
                **Die Bedienschicht** — ein eigener Kasten, und der Grund dafür
                ist Bewegung.

                Während einer Größenbewegung trägt die Canvas-Fläche
                (`__viewport`) die GRÖSSERE der beiden Breiten und hängt an der
                rechten Kante — der einzigen, die geteilt wie maximiert an
                derselben Stelle liegt. Ihre linke Kante liegt dafür um die
                Breitendifferenz daneben.

                Dieser Kasten holt LINKS diese Differenz nach (`__chrome` im
                SCSS) und deckt sich damit auf beide Kanten mit dem Panel. Alles
                darin sitzt danach von selbst richtig: rechts Verankertes steht
                still — ohne dass irgendetwas nachrechnete —, links Verankertes
                läuft mit der Kante mit. Keine Regel muss das mehr einzeln tun.

                Er darf KEINEN eigenen Stapelkontext aufmachen — kein `z-index`,
                kein `transform`, keine `opacity` unter 1. Seine Kinder ordnen
                sich einzeln gegen den Canvas ein: die Flächen unter die Notizen
                (`$canvas-cover-layer`), die Knöpfe darüber
                (`$canvas-control-layer`). In einem eigenen Kontext ginge diese
                Staffelung verloren und die Flächen lägen über allem (ADR 0008).
              */}
              <div className="project-canvas-panel__chrome">
                {/*
                  Die Randpfeile ZUERST: Bei gleichem `z-index` entscheidet die
                  DOM-Reihenfolge, und die Bedienung soll gewinnen.
                */}
                <CanvasOffscreenHints actionsWidth={actionsWidth} suppressLeft={handleActive} toolbarSide={toolbarSide} suppressAll={toolbarDragging} />
                {/* Die **Karte** liegt neben den Pfeilen, nicht in ihnen: Sie ist ein
                    Objekt, das man anfasst, und sie sind Rückmeldung (CONTEXT.md). */}
                <CanvasMap toolbarSide={toolbarSide} suppressed={toolbarDragging} />
                {/*
                  Der **Projektname** — nur maximiert. Dort ist die
                  **Aufgabenliste** nach links aus dem Bild gefahren und mit ihr
                  die Breadcrumb; nichts sagte sonst mehr, in welchem Projekt man
                  arbeitet. Geteilt steht sie daneben, und der Titel stünde
                  doppelt da.
                */}
                {panelState === 'maximized' && (
                  <div className="project-canvas-panel__project-title">
                    <CanvasProjectTitle title={projectName} titleHtml={projectNameHtml} icon={projectIcon} iconColor={projectIconColor} />
                  </div>
                )}
                <div ref={actionsRef} className="project-canvas-panel__actions">
                  <CanvasShareButton />
                  <CanvasWindowModeToggle
                    state={panelState}
                    onToggleMaximize={handleToggleMaximize}
                    onClose={handleClose}
                  />
                </div>
                {toolbarTarget}
                {toolbarPlate}
              </div>
            </CanvasFlow>
          </ReactFlowProvider>
          </div>
        </div>
      )}

      {/*
        Die **Schicht** beim Öffnen aus dem Anriss: derselbe Anriss ohne Fläche,
        an der rechten Kante festgehalten, während die Canvas-Fläche nach links
        wächst — er blendet aus, der Inhalt blendet ein (CONTEXT.md,
        Canvas-Anriss: „Er kommt und geht in Bewegung"). Nach `__canvas` im
        Baum, damit er darüber liegt; Zeiger nimmt er keine.
      */}
      {openingFromPeek && (
        <div className="project-canvas-panel__peek-ghost" aria-hidden="true">
          <CanvasPeek
            ghost
            leaving
            notes={pickPeekNotes(notes ?? [])}
            onOpenSplit={handleOpenPanel}
            onOpenMaximized={handleOpenMaximized}
          />
        </div>
      )}

      {/*
        Der **Anriss** — in zwei Lagen, aber EIN Knoten (siehe `__peek-layer`
        im SCSS): Während des Zuziehens mit Elementen liegt er als Schicht
        (`ghost`, ohne Fläche) rechts über der schrumpfenden Canvas-Fläche und
        blendet ein; steht der Canvas, ist er der Streifen selbst. Derselbe
        Platz im Baum in beiden Lagen — sonst montierte
        React ihn beim Umschalten neu, und die laufende Einblendung begänne von
        vorn. `notes` ist sicher geladen — beides verlangt es.
      */}
      {(showPeek || closingToPeek) && (
        <div className={`project-canvas-panel__peek-layer${closingToPeek ? ' project-canvas-panel__peek-layer--arriving' : ''}`}>
          <CanvasPeek
            notes={pickPeekNotes(notes ?? [])}
            onOpenSplit={handleOpenPanel}
            onOpenMaximized={handleOpenMaximized}
            entering={peekEntering}
            ghost={closingToPeek}
          />
        </div>
      )}

      {/*
        Das **Drag Handle** liegt in KEINEM Layout — weder in der Canvas-Fläche
        noch im Panel noch im Tab-Slot. Es hängt per Portal am `document.body`
        und bekommt seine Lage gemessen (ADR 0010, Begründung dort und im
        Kopfkommentar der Komponente).

        In jeder Größe vorhanden: geschlossen holt ein Zug den Canvas herein,
        maximiert die Aufgabenliste (sie ist nicht weg, sondern nach links
        ausgefahren — ProjectPage.scss), geteilt verschiebt er die Grenze.

        `isActiveTab` ist hier TRAGEND und nicht bloß eine Optimierung: Außerhalb
        des Tab-Slots versteckt dessen `display: none` nichts mehr, der Griff
        eines Hintergrund-Tabs schwebte sonst über dem sichtbaren.

        **Verdeckt von den Aufgaben-Details fällt er weg** (Nutzer-Vorgabe,
        2026-09-19): Ein Zug verschöbe dort eine Grenze, die niemand sieht. Nicht
        nur unsichtbar, sondern weg — er hängt als Portal am `body` und ließe
        sich sonst weiter greifen.
      */}
      {isActiveTab && !coveredByDetails && (
        <CanvasDragHandle
          // Am Anriss: die Lage von `split` (linke Panel-Kante), die
          // Sichtbarkeit von `closed` (dauerhaft) — siehe CanvasDragHandle.
          variant={showPeek ? 'peek' : panelState === 'closed' ? 'closed' : 'split'}
          enabled={isActiveTab}
          panel={panelElement}
          getRect={getRect}
          getState={getPanelState}
          getFloor={getDragFloor}
          // Was die Lage ändern kann, ohne dass sich eine Größe ändert.
          // Der ResizeObserver im Handle deckt den Rest ab.
          layoutKey={`${panelState}:${showPeek}:${closing}:${effectiveRatio}`}
          onChange={handleDragChange}
          onCommit={handleDragCommit}
          onDoubleClick={handleDoubleClick}
          onActiveChange={setHandleActive}
        />
      )}

    </div>
  )
}
